// ============================================================================
// PREFERRED-SUBURB FORMAT HELPERS (regex-only — no postcode table)
// ----------------------------------------------------------------------------
// watch_list_builder._parse_suburb_entry only accepts entries of the form
// "Suburb STATE POSTCODE" (e.g. "Berwick VIC 3806"). Any other format silently
// fails on the backend — the entry stays in preferredSuburbs but never enters
// watched_suburbs, so the scraper never scrapes it and no alerts fire.
//
// This module holds the cheap validation/extraction helpers so they can be
// imported anywhere (including the app-wide PortfolioDataContext) without
// pulling in the 485KB postcode table. The repair/normalize side — which
// needs postcode lookups — lives in preferredSuburbRepair.js and should be
// dynamically imported only when an invalid entry actually exists.
// ============================================================================

export const AU_STATE_RE = /(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)/i;
export const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

// FE mirror of watch_list_builder._looks_like_street_address: a street-type
// token past the FIRST word means street address, not suburb ("St Kilda"
// passes, "Smith St Footscray" doesn't). Without this mirror, a pasted
// listing address like "12 Smith Street Footscray VIC 3011" stored as a
// normal-looking Preferred chip that the backend silently refused to watch —
// no alerts, no visible cause.
const STREET_TYPE_TOKENS = new Set([
  'st', 'street', 'rd', 'road', 'ave', 'avenue',
  'blvd', 'boulevard', 'ct', 'court', 'cl', 'close',
  'cct', 'circuit', 'tce', 'terrace', 'pde', 'parade',
  'dr', 'drive', 'ln', 'lane', 'pl', 'place',
  'cres', 'crescent', 'hwy', 'highway',
]);

export function looksLikeStreetAddress(name) {
  const tokens = (name.toLowerCase().match(/[a-z]+/g)) || [];
  if (tokens.length < 2) return false;
  return tokens.slice(1).some((t) => STREET_TYPE_TOKENS.has(t));
}

export function isValidPreferredFormat(entry) {
  if (!entry || typeof entry !== 'string') return false;
  const m = entry.trim().match(/^(.+?)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\s+\d{4}$/i);
  if (!m) return false;
  // Street addresses fail backend validation even in canonical form — show
  // them as needs-attention rather than healthy.
  return !looksLikeStreetAddress(m[1].trim());
}

// Extract the bare suburb name from a canonical entry. Tolerant of legacy
// shapes like "Northcote VIC" (no postcode) or pure free-text — returns the
// best-effort name without state/postcode noise. Used for chip display and
// for matching watched-suburb names against preferred entries.
export function extractSuburbName(entry) {
  if (!entry || typeof entry !== 'string') return '';
  const m = entry.match(/^(.+?)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
  return (m ? m[1] : entry).replace(/,$/, '').trim();
}
